import React, { useState, useEffect } from "react";
import T from '../../../components/common/T';
import { useParams, useNavigate } from "react-router-dom";
import { usePurchaseOrder } from "../../../context/PurchaseOrderContext";
import { useInventory } from "../../../context/InventoryContext"; 
import OdooFormLayout from "../../../components/OdooFormLayout";
import SmartButton from "../../../components/SmartButton";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../../context/AuthContext";
import Price from "../../../components/common/Price";
import { PrinterIcon, EnvelopeIcon, TruckIcon, BanknotesIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";

import { formatDate } from "../../../utils/dateUtils";

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loadedIdRef = React.useRef(null);
  // 🟢 Brought in getPO and fetchPOById for sync logic
  const { purchaseOrders, markAsDelivered, getPO, fetchPOById } = usePurchaseOrder();
  const { warehouses } = useInventory(); 
  const { checkAccess } = useAuth();
  
  const [po, setPo] = useState(null);
  const [isSyncing, setIsSyncing] = useState(true); // 🟢 Loading state for Deep Link
  const [activeTab, setActiveTab] = useState("products");
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveData, setReceiveData] = useState({ warehouseId: "", bin: "Stock" });

  // 🟢 Deep-Link Sync logic safe against infinite loops
  useEffect(() => {
    if (!id) return;
    if (loadedIdRef.current === String(id)) return;
    loadedIdRef.current = String(id);

    const syncPO = async () => {
        setIsSyncing(true);
        const local = getPO ? getPO(id) : purchaseOrders?.find(p => String(p.id) === String(id));
        
        if (local && local.lines && local.lines.length > 0) {
            setPo(local);
            setIsSyncing(false);
        } else if (fetchPOById) {
            const remote = await fetchPOById(id);
            if (remote) setPo(remote);
            setIsSyncing(false);
        } else {
            setIsSyncing(false);
        }
    };
    syncPO();
  }, [id, getPO, fetchPOById]);

  if (isSyncing) return <div className="p-10 text-center text-gray-500"><T>Syncing Order Data...</T></div>;
  if (!po) return <div className="p-10 text-center text-gray-500"><T>Purchase Order Not Found</T></div>;

  // --- ACTIONS ---
  const handleReceiveClick = () => {
      if(warehouses.length > 0) setReceiveData({ ...receiveData, warehouseId: warehouses[0].id });
      setShowReceiveModal(true);
  };

  const confirmReceive = async () => {
      if(!receiveData.warehouseId) return toast.error("Select a Warehouse");
      
      const success = await markAsDelivered(po.id, receiveData.warehouseId, receiveData.bin);
      if(success) setShowReceiveModal(false);
  };

  const handleCreateBill = () => {
      navigate(`/admin/material/vendor-bill?poId=${po.id}`);
  };

  const ActionButtons = (
      <>
        {(po.status === "Ordered" || po.status === "Dispatched") && (
            <button 
                onClick={handleReceiveClick}
                className="bg-purple-700 text-white px-4 py-2 rounded text-xs font-bold uppercase hover:bg-purple-800 transition shadow-sm flex items-center gap-2"
            >
                <TruckIcon className="w-4 h-4" /> Receive Products
            </button>
        )}

        {po.status === "Received" && (
            <button 
                onClick={handleCreateBill}
                className="bg-green-600 text-white px-4 py-2 rounded text-xs font-bold uppercase hover:bg-green-700 transition shadow-sm flex items-center gap-2"
            >
                <BanknotesIcon className="w-4 h-4" /> Create Bill
            </button>
        )}

        <button onClick={() => navigate('/admin/material/purchase')} className="bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded text-xs font-bold uppercase hover:bg-gray-50 transition ml-2">
            Back
        </button>
      </>
  );

  return (
    <>
        <OdooFormLayout
            title={po.poNumber}
            subTitle={`Vendor: ${po.vendorName}`}
            status={po.status}
            actions={ActionButtons}
        >
            <div className="flex gap-2 mb-6 justify-end">
                <SmartButton icon={EnvelopeIcon} label="Send PO" onClick={() => toast.info("Email Feature Coming Soon")} />
                <SmartButton icon={PrinterIcon} label="Print" onClick={() => window.print()} />
            </div>

            {/* DETAILS */}
            <div className="grid grid-cols-2 gap-8 mb-8 text-sm">
                <div>
                    <p className="text-gray-500 mb-1"><T>Order Date</T></p>
                    <p className="font-bold text-gray-900">{formatDate(po.createdAt || po.order_date || Date.now())}</p>
                </div>
                <div>
                    <p className="text-gray-500 mb-1"><T>Reference PR</T></p>
                    <p className="font-bold text-purple-700">{po.prNumber || po.pr_number || "—"}</p>
                </div>
            </div>

            {/* TABS & TABLE */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-bold text-gray-700 mb-4 border-b pb-2"><T>Order Lines</T></h4>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-gray-500 text-left">
                            <th className="py-2"><T>Product</T></th>
                            <th className="py-2 text-center"><T>Quantity</T></th>
                            <th className="py-2 text-right"><T>Unit Price</T></th>
                            <th className="py-2 text-right"><T>Subtotal</T></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b border-gray-100">
                            <td className="py-3 font-medium text-gray-900">{po.materialName}</td>
                            <td className="py-3 text-center">{po.quantity}</td>
                            <td className="py-3 text-right"><Price amount={po.unitPrice} /></td>
                            <td className="py-3 text-right font-bold"><Price amount={Number(po.totalPrice)} /></td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </OdooFormLayout>

        {/* RECEIVE MODAL */}
        {showReceiveModal && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-xl w-96">
                    <h3 className="text-base font-bold mb-4 flex items-center gap-2"><ArrowDownTrayIcon className="w-5 h-5 text-purple-600"/><T> Receive Goods</T></h3>
                    
                    <div className="mb-4">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Destination Warehouse</T></label>
                        <select 
                            className="w-full border p-2 rounded outline-none"
                            value={receiveData.warehouseId}
                            onChange={(e) => setReceiveData({...receiveData, warehouseId: e.target.value})}
                        >
                            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                        </select>
                    </div>

                    <div className="mb-6">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Bin / Location</T></label>
                        <input 
                            className="w-full border p-2 rounded outline-none" 
                            value={receiveData.bin}
                            onChange={(e) => setReceiveData({...receiveData, bin: e.target.value})}
                        />
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowReceiveModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"><T>Cancel</T></button>
                        <button onClick={confirmReceive} className="px-4 py-2 text-sm bg-purple-600 text-white font-bold rounded hover:bg-purple-700"><T>Confirm Receipt</T></button>
                    </div>
                </div>
            </div>
        )}
    </>
  );
}